import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { DEFAULT_MARKETPLACE } from 'utils/constants';

export const fetchPlans = createAsyncThunk('clients/getPlans', async () => {
  const response = await axios.get('/agency/invoicing/list?operation=plans');
  return response.data;
  //return response.data.data;
});

export const fetchAddons = createAsyncThunk(
  'clients/getAddons',
  async (filter = '') => {
    let filterUrl = filter !== '' ? `&filter=${filter}` : '';
    const response = await axios.get(
      `/agency/invoicing/list?operation=addons${filterUrl}`
    );
    return response.data.data;
  }
);

export const fetchCurrencies = createAsyncThunk(
  'clients/getCurrency',
  async () => {
    const response = await axios.get('/agency/invoicing/currency');
    return response.data.output;
  }
);

export const fetchExchangeRates = createAsyncThunk(
  'clients/getExchangeRates',
  async () => {
    const response = await axios.get(
      '/agency/invoicing/currency/exchangerates'
    );
    return response.data.data;
  }
);

//IN USE
export const fetchMarketplaces = createAsyncThunk(
  'clients/getMarketplaces',
  async (params) => {
    const response = await axios.get(`/agency/marketplaces`, { params });
    return { data: response.data.data };
  }
);

export const fetchSalesPersons = createAsyncThunk(
  'clients/getSalesPersons',
  async () => {
    const response = await axios.get(
      `/agency/employees/roles/sales administrator`
    );
    return { data: response.data.data };
  }
);

// Used BY ClientMigration
export const getInitialSubscriptionFormData = createAsyncThunk(
  'clients/getInitialSubscriptionFormData',
  async (id = 0, thunkAPI) => {
    let batchJobs = [
      thunkAPI.dispatch(fetchPlans()),
      thunkAPI.dispatch(fetchMarketplaces()),
      thunkAPI.dispatch(fetchAddons()),
      thunkAPI.dispatch(fetchCurrencies()),
      //thunkAPI.dispatch(fetchExchangeRates()),
      thunkAPI.dispatch(fetchSalesPersons()),
    ];

    const response = await Promise.all(batchJobs);

    return response.data;
  }
);

export const clientsSlice = createSlice({
  name: 'clients',
  initialState: {
    defaultMarketplaceId: DEFAULT_MARKETPLACE, //US
    agencyClients: [],
    unAssignedAgencyClients: [],
    clients: [],
    paginationParams: {
      page: 1,
      pageSize: 30,
      search: '',
      sort: 'client:asc',
      migrateOnly: false,
      status: '',
    },
    plans: [],
    currencies: [],
    exchangeRates: [],
    marketplaces: [],
    salesPersons: [],
    addons: [],
    loading: false,
    error: null,
    agencyClient: null,
    dataLoaded: false,
    clientLoaded: false,
    hasNewAddons: false,
    invoiceEmails: null,
    errors: null,
    prerequisitesDataLoaded: {
      plans: false,
      marketplaces: false,
      addons: false,
      currencies: false,
      salesPersons: false,
      exchangeRates: true,
    },
  },
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setDataLoaded: (state, action) => {
      state.dataLoaded = action.payload;
    },
    setClientLoaded: (state, { payload }) => {
      state.clientLoaded = payload;
    },
    checkNewAddons: (state, { payload }) => {
      if (payload.some((p) => p === 0)) {
        state.hasNewAddons = true;
      } else {
        state.hasNewAddons = false;
      }
    },
    setPaginationParams: (state, action) => {
      state.paginationParams = action.payload;
    },
    setErrors: (state, { payload }) => {
      state.errors = payload;
    },
    setClientList: (state, action) => {
      state.clients = action.payload;
    },
  },
  extraReducers: {
    [fetchPlans.fulfilled]: (state, { payload }) => {
      state.plans = payload.data;
      state.prerequisitesDataLoaded.plans = true;
    },
    [fetchAddons.fulfilled]: (state, { payload }) => {
      state.addons = payload;
      state.prerequisitesDataLoaded.addons = true;
    },
    [fetchCurrencies.fulfilled]: (state, { payload }) => {
      state.currencies = payload;
      state.prerequisitesDataLoaded.currencies = true;
    },
    [fetchExchangeRates.fulfilled]: (state, { payload }) => {
      state.exchangeRates = payload;
      state.prerequisitesDataLoaded.exchangeRates = true;
    },
    [fetchMarketplaces.fulfilled]: (state, { payload }) => {
      state.marketplaces = payload.data;
      state.prerequisitesDataLoaded.marketplaces = true;
    },
    [getInitialSubscriptionFormData.fulfilled]: (state, { payload }) => {
      state.dataLoaded = true;
    },
    [fetchSalesPersons.fulfilled]: (state, { payload }) => {
      state.salesPersons = payload.data;
      state.prerequisitesDataLoaded.salesPersons = true;
    },
  },
});

export const {
  setLoading,
  setDataLoaded,
  setClientLoaded,
  checkNewAddons,
  setPaginationParams,
  setErrors,
  setClientList,
} = clientsSlice.actions;

// used by SelectClient
export const getClientAsync = (search) => async (dispatch) => {
  try {
    await dispatch(setLoading(true));

    const res = await axios.get(
      `/agency/client?status=subscribed&pageSize=1000&sort=client:asc&search=${
        search ? search : ''
      }`
    );
    await dispatch(setClientList(res.data.data));
    await dispatch(setLoading(false));
    return res.data.data;
  } catch (error) {
    const message =
      error.response.status === 500
        ? 'Whoops. Something went wrong!'
        : error.response.status === 204
        ? 'No Clients Available!'
        : error.response.data.message;
    await dispatch(setLoading(false));
    return {};
  }
};

//used by creditnotes
export const selectClientList = (state) => state.clients.clients;

export default clientsSlice.reducer;
